export const CURRENT_PERMISSION_CATALOG: Record<string, readonly string[]> = {
  order: ['access', 'create', 'edit', 'approve', 'delete', 'own_data'],
  smart_inbox: ['access'],
  product: ['access', 'create', 'edit', 'delete'],
  catalog: ['access', 'create', 'edit', 'delete'],
  company: ['access', 'create', 'edit', 'delete'],
  knowledge_base: ['access', 'create', 'edit', 'delete'],
  workflow: ['access', 'create', 'edit', 'delete'],
  settings_team: ['access'],
  settings_company: ['access'],
  // `edit` sets the tenant MFA policy and resets another user's factor. Not
  // baseline: the backend grants it only to the protected super-admin role.
  settings_security: ['access', 'edit'],
};

/**
 * Baseline modules whose access toggle is always-on (disabled in the role editor).
 * The backend enforces `access:*` via JWT claims; the migration grants it to every role.
 */
const BASELINE_MODULE_NAMES = new Set(['order', 'product', 'catalog', 'company']);

const CURRENT_MODULE_NAMES = new Set(Object.keys(CURRENT_PERMISSION_CATALOG));
const CURRENT_PERMISSION_NAMES = new Set(
  Object.entries(CURRENT_PERMISSION_CATALOG).flatMap(([moduleName, actions]) =>
    actions.map((action) => `${action}:${moduleName}`)
  )
);

export const OWNERSHIP_CAPABLE_MODULES: readonly string[] = ['order'];

export function isDataScopePermission(permissionName: string): boolean {
  return (permissionName ?? '').split(':')[0]?.toLowerCase() === 'own_data';
}

export function buildPermissionName(moduleName: string, action = 'access'): string {
  return `${action}:${moduleName}`;
}

export function normalizePermissionNames(permissionNames: string[] | null | undefined): string[] {
  const normalizedPermissions = new Set<string>();

  for (const permissionName of permissionNames ?? []) {
    if (permissionName && CURRENT_PERMISSION_NAMES.has(permissionName)) {
      normalizedPermissions.add(permissionName);
    }
  }

  return Array.from(normalizedPermissions);
}

export function isKnownPermissionModule(moduleName: string): boolean {
  return CURRENT_MODULE_NAMES.has(moduleName);
}

export function moduleRequiresAccessPermission(moduleName: string): boolean {
  if (!isKnownPermissionModule(moduleName)) {
    return false;
  }

  if (BASELINE_MODULE_NAMES.has(moduleName)) {
    return false;
  }

  return CURRENT_PERMISSION_CATALOG[moduleName].includes('access');
}

export function hasPermission(
  permissions: string[] | null | undefined,
  permissionName: string
): boolean {
  return normalizePermissionNames(permissions).includes(permissionName);
}

export function canAccessModule(
  permissions: string[] | null | undefined,
  moduleName: string
): boolean {
  if (!isKnownPermissionModule(moduleName)) {
    return false;
  }

  if (!moduleRequiresAccessPermission(moduleName)) {
    return true;
  }

  return hasPermission(permissions, buildPermissionName(moduleName));
}

export function canDo(
  permissions: string[] | null | undefined,
  moduleName: string,
  action = 'access'
): boolean {
  if (!isKnownPermissionModule(moduleName)) {
    return false;
  }

  if (action === 'access') {
    return canAccessModule(permissions, moduleName);
  }

  if (!hasPermission(permissions, buildPermissionName(moduleName, action))) {
    return false;
  }

  if (!moduleRequiresAccessPermission(moduleName)) {
    return true;
  }

  return canAccessModule(permissions, moduleName);
}
